import React, { useState, useEffect, useCallback } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import {
  getAdminOrganizationDetail,
  updateOrganizationPlan,
  lockOrganization,
  unlockOrganization,
  deleteOrganization,
} from '../../utils/superadminApi';

const FEATURE_KEYS = ['jira', 'slack', 'sso', 'github'];

const StatBox = ({ label, value }) => (
  <Grid item xs={6} sm={4}>
    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
      <Typography variant="h6">{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
  </Grid>
);

const OrganizationDetailDrawer = ({ orgId, open, onClose, onChanged, onDeleted }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // Editable plan state
  const [plan, setPlan] = useState('free');
  const [maxTeams, setMaxTeams] = useState('');
  const [maxMembers, setMaxMembers] = useState('');
  const [features, setFeatures] = useState({});
  const [notes, setNotes] = useState('');

  // Lock dialog state
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [lockReason, setLockReason] = useState('');

  // Delete-org dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminOrganizationDetail(orgId);
      setDetail(data);
      const planRow = data.organization.plan || {};
      setPlan(planRow.plan || 'free');
      const limits = planRow.limits || {};
      setMaxTeams(limits.maxTeams == null ? '' : String(limits.maxTeams));
      setMaxMembers(limits.maxMembers == null ? '' : String(limits.maxMembers));
      setFeatures(planRow.features || {});
      setNotes(planRow.notes || '');
    } catch (err) {
      console.error('Error loading organization detail:', err);
      setError(err.response?.data?.message || 'Failed to load organization detail.');
    }
    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    if (open && orgId) {
      loadDetail();
      setSaveMessage(null);
    }
  }, [open, orgId, loadDetail]);

  const handleFeatureToggle = (key) => {
    setFeatures((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaveMessage(null);
    try {
      const limits = {
        maxTeams: maxTeams === '' ? null : parseInt(maxTeams, 10),
        maxMembers: maxMembers === '' ? null : parseInt(maxMembers, 10),
      };
      await updateOrganizationPlan(orgId, { plan, limits, features, notes });
      setSaveMessage('Saved.');
      await loadDetail();
      if (onChanged) onChanged();
    } catch (err) {
      console.error('Error saving plan:', err);
      setError(err.response?.data?.message || 'Failed to save plan.');
    }
    setSaving(false);
  };

  const handleLock = async () => {
    setSaving(true);
    setError(null);
    try {
      await lockOrganization(orgId, lockReason);
      setLockDialogOpen(false);
      setLockReason('');
      await loadDetail();
      if (onChanged) onChanged();
    } catch (err) {
      console.error('Error locking organization:', err);
      setError(err.response?.data?.message || 'Failed to lock organization.');
    }
    setSaving(false);
  };

  const handleUnlock = async () => {
    setSaving(true);
    setError(null);
    try {
      await unlockOrganization(orgId);
      await loadDetail();
      if (onChanged) onChanged();
    } catch (err) {
      console.error('Error unlocking organization:', err);
      setError(err.response?.data?.message || 'Failed to unlock organization.');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await deleteOrganization(orgId);
      setDeleteDialogOpen(false);
      // Close the drawer and refresh the list; the org no longer exists.
      if (onDeleted) {
        onDeleted(orgId);
      } else {
        if (onChanged) onChanged();
        if (onClose) onClose();
      }
    } catch (err) {
      console.error('Error deleting organization:', err);
      setError(err.response?.data?.message || 'Failed to delete organization.');
      setDeleteDialogOpen(false);
    }
    setDeleting(false);
  };

  const org = detail?.organization;
  const isLocked = !!(org?.plan && org.plan.isLocked);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: 340, sm: 460 }, p: 3 }} role="presentation">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5">{org ? org.name : 'Organization'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {org && !loading && (
          <>
            {isLocked && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Workspace suspended{org.plan.lockedReason ? `: ${org.plan.lockedReason}` : ''}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {org.domain} &middot; created {new Date(org.createdAt).toLocaleDateString()}
            </Typography>

            {/* Usage */}
            <Typography variant="subtitle1" gutterBottom>Usage</Typography>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              <StatBox label="Members" value={detail.usage.memberCount} />
              <StatBox label="Teams" value={detail.usage.teamCount} />
              <StatBox label="Active cycles" value={detail.usage.activeCycleCount} />
              <StatBox label="Cycles" value={detail.usage.cycleCount} />
              <StatBox label="Bugs" value={detail.usage.bugCount} />
              <StatBox label="Sessions" value={detail.usage.sessionCount} />
            </Grid>

            {/* Integrations */}
            <Typography variant="subtitle1" gutterBottom>Integrations</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
              <Chip label="Slack" color={detail.integrations.slack ? 'success' : 'default'} size="small" />
              <Chip label="GitHub" color={detail.integrations.github ? 'success' : 'default'} size="small" />
              <Chip label="SSO" color={detail.integrations.sso ? 'success' : 'default'} size="small" />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Plan management */}
            <Typography variant="subtitle1" gutterBottom>Plan &amp; Limits</Typography>
            <TextField
              select
              fullWidth
              label="Plan"
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="free">Free</MenuItem>
              <MenuItem value="pro">Pro</MenuItem>
              <MenuItem value="enterprise">Enterprise</MenuItem>
            </TextField>
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max teams"
                  value={maxTeams}
                  onChange={(e) => setMaxTeams(e.target.value)}
                  helperText="Blank = unlimited"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max members"
                  value={maxMembers}
                  onChange={(e) => setMaxMembers(e.target.value)}
                  helperText="Blank = unlimited"
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Feature Gates</Typography>
            <Box sx={{ mb: 2 }}>
              {FEATURE_KEYS.map((key) => (
                <FormControlLabel
                  key={key}
                  control={<Switch checked={!!features[key]} onChange={() => handleFeatureToggle(key)} />}
                  label={key.toUpperCase()}
                />
              ))}
            </Box>

            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Internal notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ mb: 2 }}
            />

            {saveMessage && <Alert severity="success" sx={{ mb: 2 }}>{saveMessage}</Alert>}

            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                Save changes
              </Button>
              {isLocked ? (
                <Button variant="outlined" color="success" onClick={handleUnlock} disabled={saving}>
                  Unlock
                </Button>
              ) : (
                <Button variant="outlined" color="error" onClick={() => setLockDialogOpen(true)} disabled={saving}>
                  Lock workspace
                </Button>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Danger zone: permanently delete a spam/empty org and its data. */}
            <Typography variant="subtitle1" gutterBottom color="error">Danger zone</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Permanently delete this workspace and all of its members, teams, cycles and sessions.
              This cannot be undone.
            </Typography>
            <Button variant="contained" color="error" onClick={() => setDeleteDialogOpen(true)} disabled={saving || deleting}>
              Delete workspace
            </Button>
          </>
        )}
      </Box>

      <Dialog open={lockDialogOpen} onClose={() => setLockDialogOpen(false)}>
        <DialogTitle>Lock workspace?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Members will be blocked from making changes (writes) until the workspace is unlocked.
            Read access remains available.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Reason (optional)"
            value={lockReason}
            onChange={(e) => setLockReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLockDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleLock} disabled={saving}>
            Lock
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete workspace?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes <strong>{org?.name}</strong>
            {org?.domain ? ` (${org.domain})` : ''} and all of its members, teams, cycles, sessions
            and integrations. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default OrganizationDetailDrawer;
